'use client';

import { createContext, useContext, useState, useCallback, useRef, useEffect, ReactNode } from 'react';
import { createClient } from '@/lib/supabase/client';

export type NotificationType = 'success' | 'error' | 'warning' | 'info';

export interface AppNotification {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  time: Date;
  read: boolean;
  /** Deep link URL for quick navigation (e.g. failed post) */
  actionUrl?: string;
  actionLabel?: string;
}

export interface ToastItem {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  actionUrl?: string;
  actionLabel?: string;
  duration?: number;
}

interface NotificationContextValue {
  notifications: AppNotification[];
  toasts: ToastItem[];
  unreadCount: number;
  addNotification: (n: Omit<AppNotification, 'id' | 'time' | 'read'>) => void;
  markAllRead: () => void;
  markRead: (id: string) => void;
  dismissToast: (id: string) => void;
  /** Convenience helpers */
  notifyNewEscalation: (
    ticketId: string,
    title: string,
    jobId?: string | null,
    priority?: string,
    sourceDepartment?: string
  ) => void;
}

const NotificationContext = createContext<NotificationContextValue | null>(null);

let idCounter = 0;
const genId = () => `notif-${Date.now()}-${++idCounter}`;

// Maps a DB `notifications` row into the in-app shape the bell renders.
function dbRowToNotification(r: any): AppNotification {
  return {
    id: r.id,
    type: (r.type as NotificationType) || 'info',
    title: r.title || '',
    message: r.message || '',
    time: r.created_at ? new Date(r.created_at) : new Date(),
    read: !!r.read,
    actionUrl: r.action_url || undefined,
    actionLabel: r.action_label || undefined,
  };
}

export function NotificationProvider({ children }: { children: ReactNode }) {
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timerRefs = useRef<Record<string, ReturnType<typeof setTimeout>>>({});
  // DB-backed notification ids we've already ingested — dedupes the hydrate
  // load vs. realtime INSERTs, and marks which ids to persist read-state for.
  const seenDbIds = useRef<Set<string>>(new Set());

  const dismissToast = useCallback((id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
    if (timerRefs.current[id]) {
      clearTimeout(timerRefs.current[id]);
      delete timerRefs.current[id];
    }
  }, []);

  const pushToast = useCallback((toast: ToastItem) => {
    setToasts(prev => [toast, ...prev].slice(0, 5));
    const duration = toast.duration ?? 5000;
    timerRefs.current[toast.id] = setTimeout(() => {
      dismissToast(toast.id);
    }, duration);
  }, [dismissToast]);

  const addNotification = useCallback((n: Omit<AppNotification, 'id' | 'time' | 'read'>) => {
    const id = genId();
    const notification: AppNotification = { ...n, id, time: new Date(), read: false };
    setNotifications(prev => [notification, ...prev].slice(0, 50));
    // Also show as toast
    pushToast({
      id: genId(),
      type: n.type,
      title: n.title,
      message: n.message,
      actionUrl: n.actionUrl,
      actionLabel: n.actionLabel,
      duration: n.type === 'error' ? 8000 : 5000,
    });
  }, [pushToast]);

  const markAllRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    // Persist read-state for DB-backed notifications (fire-and-forget).
    const ids = Array.from(seenDbIds.current);
    if (ids.length) {
      createClient().from('notifications').update({ read: true }).in('id', ids)
        .then(({ error }: { error: any }) => { if (error) console.error('[notif] markAllRead persist failed:', error.message); });
    }
  }, []);

  const markRead = useCallback((id: string) => {
    setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
    if (seenDbIds.current.has(id)) {
      createClient().from('notifications').update({ read: true }).eq('id', id)
        .then(({ error }: { error: any }) => { if (error) console.error('[notif] markRead persist failed:', error.message); });
    }
  }, []);

  const notifyNewEscalation = useCallback((
    ticketId: string,
    title: string,
    jobId?: string | null,
    priority: string = 'high',
    sourceDepartment: string = 'Logistics'
  ) => {
    const jobSuffix = jobId ? ` · Job ${jobId}` : '';
    const actionUrl = `/escalation-management-dashboard?ticket=${ticketId}`;
    const isCritical = priority === 'critical' || priority === 'high';
    const isFinanceDept = ['Billing', 'Finance', 'Financial'].includes(sourceDepartment);

    // ── Operations Head notification (always sent) ──────────────────────────
    addNotification({
      type: 'warning',
      title: `🚨 [Ops Head] Escalation — ${ticketId}`,
      message: `${title}${jobSuffix} · Priority: ${priority.toUpperCase()} · Dept: ${sourceDepartment}. Review and assign resolution owner.`,
      actionUrl,
      actionLabel: 'Open Ticket',
    });

    // ── Finance Lead notification (high/critical priority OR finance-related dept) ──
    if (isCritical || isFinanceDept) {
      addNotification({
        type: isCritical ? 'error' : 'warning',
        title: `💰 [Finance Lead] Escalation — ${ticketId}`,
        message: `${title}${jobSuffix} · Priority: ${priority.toUpperCase()} · Dept: ${sourceDepartment}. Financial impact review required.`,
        actionUrl,
        actionLabel: 'Open Ticket',
      });
    }

    // ── Affected PM notification (always sent) ──────────────────────────────
    addNotification({
      type: 'warning',
      title: `📋 [PM] Escalation — ${ticketId}`,
      message: `${title}${jobSuffix} has been escalated from ${sourceDepartment}. Priority: ${priority.toUpperCase()}. Monitor for updates.`,
      actionUrl,
      actionLabel: 'Open Ticket',
    });
  }, [addNotification]);

  // ── DB-backed feed: hydrate recent notifications + subscribe to live INSERTs ──
  // Bridges server-originated / cross-session events (e.g. a customer signing an
  // estimate on the public /sign page) into the bell for every open CRM session.
  useEffect(() => {
    const supabase = createClient();
    let cancelled = false;

    // Hydrate the last 50 so a staffer who opens the CRM after the event still
    // sees it. Historical rows are NOT toasted — only live INSERTs are.
    supabase
      .from('notifications')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(50)
      .then(({ data, error }: { data: any; error: any }) => {
        if (cancelled || error || !data) return;
        const rows = data.filter((r: any) => !seenDbIds.current.has(r.id));
        rows.forEach((r: any) => seenDbIds.current.add(r.id));
        if (rows.length) {
          setNotifications(prev => [...rows.map(dbRowToNotification), ...prev].slice(0, 50));
        }
      });

    const channel = supabase
      .channel('notifications-feed')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'notifications' },
        (payload: any) => {
          const r = payload.new;
          if (!r || seenDbIds.current.has(r.id)) return;
          seenDbIds.current.add(r.id);
          const notif = dbRowToNotification(r);
          setNotifications(prev => [notif, ...prev].slice(0, 50));
          // Live event → also surface a toast.
          pushToast({
            id: genId(),
            type: notif.type,
            title: notif.title,
            message: notif.message,
            actionUrl: notif.actionUrl,
            actionLabel: notif.actionLabel,
            duration: notif.type === 'error' ? 8000 : 5000,
          });
        },
      )
      .subscribe();

    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, [pushToast]);

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <NotificationContext.Provider value={{
      notifications,
      toasts,
      unreadCount,
      addNotification,
      markAllRead,
      markRead,
      dismissToast,
      notifyNewEscalation,
    }}>
      {children}
    </NotificationContext.Provider>
  );
}

export function useNotifications() {
  const ctx = useContext(NotificationContext);
  if (!ctx) throw new Error('useNotifications must be used within NotificationProvider');
  return ctx;
}
